import { useState } from "react";
import type { DiaryPaymentClassification } from "../booking/diaryPayments.js";
import { setBookingPaymentOverride, undoBookingPaymentOverride } from "../booking/paymentOverrides.js";
import { usePublishActionOutcome } from "../ui/ActionConfirmation.js";

export function PaymentOverridePanel({ appointmentId, payment, onBusyChange, onChanged }: {
  appointmentId: number;
  payment: DiaryPaymentClassification;
  onBusyChange: (busy: boolean) => void;
  onChanged: () => void;
}) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const publish = usePublishActionOutcome();
  const override = payment.payment_override;

  async function submit() {
    if (busy || (!override && !payment.booking_id)) return;
    setBusy(true);
    onBusyChange(true);
    setProblem(null);
    try {
      if (override) {
        await undoBookingPaymentOverride(appointmentId, override, reason.trim());
        publish({ variant: "success", title: "Payment override undone", description: "The lesson’s payment status will be checked again.", details: [{ value: "The override remains in the audit history." }] });
      } else {
        const result = await setBookingPaymentOverride(appointmentId, payment.booking_id!, reason.trim());
        if (!result?.id || result.booking_id !== payment.booking_id) throw new Error("The payment override was not confirmed. Refresh the lesson to check its status.");
        publish({ variant: "success", title: "Lesson marked paid in full", description: `£${result.amount_waived} waived${result.reason.trim() ? ` · ${result.reason}` : ""}`, details: [{ value: "Paid by override. No payment was collected." }] });
      }
      onChanged();
    } catch (cause) {
      setProblem(cause instanceof Error ? cause.message : "The override could not be confirmed. Refresh the lesson to check its status.");
    } finally {
      setBusy(false);
      onBusyChange(false);
    }
  }

  return <div className="stack">
    <h3>{override ? "Undo payment override" : "Mark paid in full"}</h3>
    {override ? <p>Remove this override and use the lesson’s current payment records again? The £{override.amount_waived} waiver and reason stay in the audit history.</p> : <>
      <p>Current outstanding balance: £{payment.amount_due}. The amount will be checked again when you save.</p>
      <p>The lesson stays Paid · Override until you undo it, including after later price or payment changes.</p>
      <p className="small muted">This records a waiver in the Hub. No payment is collected, and Amelia prices and payment records stay unchanged.</p>
    </>}
    <label>Note (optional)
      <div className="row" aria-label="Note suggestions">
        {["Rate difference", "Goodwill", "Agreed discount"].map((suggestion) => (
          <button key={suggestion} type="button" className="quiet small" disabled={busy} onClick={() => setReason(suggestion)}>{suggestion}</button>
        ))}
      </div>
      <textarea value={reason} onChange={(event) => setReason(event.target.value)} maxLength={1000} disabled={busy} placeholder={override ? "Why are you removing this waiver?" : "Moved lesson, original £30 rate"} />
    </label>
    {problem && <div className="error" role="alert">{problem}</div>}
    <button type="button" disabled={busy || (!override && !payment.booking_id)} onClick={() => void submit()}>
      {busy ? "Saving…" : override ? "Confirm undo override" : "Confirm paid in full"}
    </button>
  </div>;
}
